import React, { useEffect, useCallback, useState, useMemo } from 'react'
import { useParams, useHistory, Prompt } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import CircularProgress from '@material-ui/core/CircularProgress'
import Box from '@material-ui/core/Box'

import SearchDetailsPage from 'ui/pages/searchDetail'
import Item from 'ui/modules/lists/vendorsList/item'

import useQueryParams from 'hooks/useQueryParams'
import useTranslation from 'hooks/useTranslations'

import {
  fetchSearchDetails,
  fetchSearchOptions,
  clearSearchOptions,
  setSearchToSave,
  saveSearch,
  setIsVendorInMyNetwork,
  exportToCSV,
  clearSearchDetails,
} from 'services/searches/actions'
import {
  getSearchDetailsFilters,
  getSearchDetailsResults,
  getIsSearchDetailsFetching,
  getSearchOptions,
  getSearchDetailsError,
} from 'services/searches/selectors'
import { getSearchConfig } from 'services/config/selectors'
import {
  getSearchFiltersByParams,
  getSearchURLParams,
  getSearchBodyData,
  getExistingSearchFilters,
} from 'services/searches/business'
import { openSaveNewSearchModal } from 'services/modal/actions'
import { showSnackbar } from 'services/snackbar/actions'
import * as actionTypesSearches from 'services/searches/action-types'

const SearchDetails = () => {
  const t = useTranslation()
  const { id } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const isFetching = useSelector(getIsSearchDetailsFetching)
  const filtersFromSearch = useSelector(getSearchDetailsFilters)
  const results = useSelector(getSearchDetailsResults)
  const autocompleteOptions = useSelector(getSearchOptions)
  const searchConfig = useSelector(getSearchConfig)
  const genericError = useSelector(getSearchDetailsError)
  const query = useQueryParams()
  const [canSave, setCanSave] = useState(!id)
  const [searchConfigParams, setSearchConfigParams] = useState('')
  const [isInitialFetching, setIsInitialFetching] = useState(true)
  const [searchFilters, setSearchFilters] = useState({})
  const [isEditing, setIsEditing] = useState(false)
  const [unsavedConfig, setUnsavedConfig] = useState('')
  const [keyword, setKeyword] = useState('')
  const [filters, setFilters] = useState(() => {
    const queryHomologated = query.get('homologated')
    const homologatedFilter =
      queryHomologated == null ? {} : { homologated: !!queryHomologated }
    return { ...homologatedFilter }
  })

  useEffect(() => {
    if (!isFetching && isInitialFetching) setIsInitialFetching(false)
  }, [isFetching, isInitialFetching])

  useEffect(() => {
    if (id) {
      setIsInitialFetching(true)
      dispatch(
        fetchSearchDetails({
          id: isEditing ? undefined : id,
          keyword,
          filters,
          data: searchConfigParams,
        }),
      )
      updateURLParams()
      setCanSave(false)
    }

    return () => {
      setSearchConfigParams('')
      setSearchFilters({})
      setIsEditing(false)
      setUnsavedConfig('')
      dispatch(clearSearchDetails())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (id || searchConfigParams) {
      dispatch(
        fetchSearchDetails({
          id: isEditing ? undefined : id,
          keyword,
          filters,
          data: searchConfigParams,
        }),
      )
      updateURLParams()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchConfigParams])

  useEffect(() => {
    const paramsSearchFilters =
      id && !isEditing
        ? getExistingSearchFilters(filtersFromSearch, searchConfig)
        : getSearchFiltersByParams(query, searchConfig)
    setSearchFilters(paramsSearchFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFromSearch])

  useEffect(() => {
    setSearchConfigParams(getSearchURLParams(searchFilters))
    updateURLParams()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters])

  const handleGoToPage = useCallback(
    page => {
      dispatch(
        fetchSearchDetails({
          id: isEditing ? undefined : id,
          page,
          keyword,
          filters,
          data: searchConfigParams,
        }),
      )
    },
    [dispatch, filters, id, isEditing, keyword, searchConfigParams],
  )

  const handleSearch = newKeyword => {
    setKeyword(newKeyword)
    dispatch(
      fetchSearchDetails({
        id: isEditing ? undefined : id,
        keyword: newKeyword,
        filters,
        data: searchConfigParams,
      }),
    )
  }

  const handleGetSearchOptions = keyword => {
    const name = filtersFromSearch.name || ''
    dispatch(
      fetchSearchOptions({
        id,
        name,
        keyword,
        filters,
        data: searchConfigParams,
      }),
    )
  }

  const handleClearSearchOptions = () => {
    dispatch(clearSearchOptions())
  }

  const updateURLParams = useCallback(
    ({ newFilters = filters } = {}) => {
      const urlParams = searchConfigParams ? [searchConfigParams] : []
      if (newFilters.hasOwnProperty('homologated')) {
        urlParams.push(`homologated=${newFilters.homologated}`)
      }
      history.replace({ search: urlParams.join('&') })
    },
    [filters, history, searchConfigParams],
  )

  const handleSetFilters = useCallback(
    (newFilters, newKeyword = keyword) => {
      setFilters(newFilters)
      updateURLParams({ newFilters })
      dispatch(
        fetchSearchDetails({
          id: isEditing ? undefined : id,
          keyword: newKeyword,
          filters: newFilters,
          data: searchConfigParams,
        }),
      )
    },
    [dispatch, id, isEditing, keyword, searchConfigParams, updateURLParams],
  )

  const handleSaveSearch = useCallback(() => {
    const data = getSearchBodyData(unsavedConfig || searchFilters)
    if (id) {
      dispatch(saveSearch({ id, data }))
      setCanSave(false)
    } else {
      dispatch(setSearchToSave({ id, data }))
      dispatch(openSaveNewSearchModal())
    }
  }, [dispatch, id, searchFilters, unsavedConfig])

  const handleAdvancedSearch = useCallback(
    params => {
      setSearchConfigParams(params)

      // Force search when params are same
      if (params === searchConfigParams) {
        dispatch(
          fetchSearchDetails({
            id: isEditing ? undefined : id,
            keyword,
            filters,
            data: searchConfigParams,
          }),
        )
      } else {
        if (!canSave) setCanSave(true)
        if (!isEditing && id) setIsEditing(true)
      }
    },
    [canSave, dispatch, filters, id, isEditing, keyword, searchConfigParams],
  )

  const handleAddVendor = useCallback(
    id => {
      const vendor = results.find(result => result.id === id)

      if (!vendor) return

      dispatch(setIsVendorInMyNetwork(id, true))

      dispatch(
        showSnackbar({
          message: 'vendors:invitation-send',
          actionToDo: {
            type: actionTypesSearches.ADD_VENDOR,
            payload: { identification_code: vendor.identification_code },
          },
          actionOnCancel: {
            type: actionTypesSearches.FAKE_ADD_VENDOR,
            payload: { id, isInMyNetwork: false },
          },
        }),
      )
    },
    [dispatch, results],
  )

  const handleCSVExport = useCallback(() => {
    dispatch(
      exportToCSV({
        id,
        keyword,
        filters,
        data: searchConfigParams,
        translator: t,
      }),
    )
  }, [dispatch, filters, id, keyword, searchConfigParams, t])

  const handleRemoveFilters = useCallback(() => {
    setKeyword('')
    handleSetFilters({}, '')
  }, [handleSetFilters])
  const hasFilters = useMemo(() => Object.keys(filters).length || keyword, [
    filters,
    keyword,
  ])

  const errorType = useMemo(() => {
    if (isFetching) return { type: '' }
    if (!results.length && hasFilters)
      return { type: 'noResultsFilter', action: handleRemoveFilters }
    if (!results.length) return { type: 'noResultsSearchDetail' }
    return {}
  }, [handleRemoveFilters, hasFilters, isFetching, results.length])

  const handleUnappliedConfigChange = useCallback(params => {
    setUnsavedConfig(params)
    setCanSave(
      params.CPV.length > 0 ||
        params.documents.length > 0 ||
        params.vendor_types.length > 0,
    )
  }, [])

  return !isInitialFetching ? (
    <>
      <Prompt
        when={!!(canSave && id)}
        message={location => {
          return location.pathname.startsWith(`/app/searches/${id}`) || !id
            ? true
            : t('searches:confirm-exit')
        }}
      />

      <SearchDetailsPage
        isLoading={isFetching}
        searchConfig={searchConfig}
        totalPages={filtersFromSearch.pages}
        currentPage={filtersFromSearch.page}
        goToPage={handleGoToPage}
        onSearch={handleSearch}
        searchOptions={autocompleteOptions}
        getSearchOptions={handleGetSearchOptions}
        clearSearchOptions={handleClearSearchOptions}
        totalVendors={filtersFromSearch.total}
        filters={filters}
        setFilters={handleSetFilters}
        initialSearchParams={searchFilters}
        onSaveSearch={handleSaveSearch}
        canSave={canSave}
        onAdvancedSearch={handleAdvancedSearch}
        title={
          id ? filtersFromSearch.name || '' : t('searches:new-search-title')
        }
        onCSVExport={handleCSVExport}
        genericError={genericError}
        searchValue={keyword}
        setSearchValue={setKeyword}
        errorType={errorType}
        onConfigChange={handleUnappliedConfigChange}
      >
        {results.map(result => (
          <Item
            key={result.id}
            title={result.name}
            id={result.id}
            isHomologated={result.homologated}
            isInMyNetwork={result.is_in_my_network}
            onAddVendor={handleAddVendor}
            searchName={filtersFromSearch.name}
          />
        ))}
      </SearchDetailsPage>
    </>
  ) : (
    <Box
      height="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress color="secondary" />
    </Box>
  )
}

export default SearchDetails
